// src/components/tickets/detail/Composer.tsx
//
// The ticket composer (Story 4.3 AC 7/8). Two explicit modes as tabs — Response
// (default, states the recipient) and Internal note (yellow-walled, "Internal —
// not visible to rep") — with SEPARATE text buffers per tab so text typed as a
// note can never silently become a response (the separate-buffer invariant).
//
// Mounts under ThreadList (4.2's reserved seam). The header's Respond / Internal
// note actions and DraftPanel's "Use draft" drive it through an imperative handle
// (activateResponse / activateNote / useDraft). A response opens the SendConfirm
// step (nothing emails a rep without that click); an internal note submits
// directly. On success the buffer clears and an inline confirmation shows; the
// status/thread re-render from the server via the hook's silent refetch (no
// optimistic state). On a business failure the mapped copy shows inline and the
// text is preserved for retry.

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { SendConfirm } from './SendConfirm'
import { draftEditedForDisplay } from '../../../utils/draftCompare'

export interface ComposerHandle {
  activateResponse: () => void
  activateNote: () => void
  useDraft: () => void
}

interface ComposerProps {
  // The ticket's stored draft (null when none). Starting material for the FIRST
  // response only.
  draft: string | null
  // The rep display name, or null when the caller has no registered rep.
  repName: string | null
  // True when the ticket has no prior response message (drives the confirm-step
  // edited/unedited line — mirrors the server's sent_unedited rule).
  isFirstResponse: boolean
  responding: boolean
  noting: boolean
  onRespond: (body: string) => Promise<void>
  onNote: (body: string) => Promise<void>
}

type Tab = 'response' | 'note'

export const Composer = forwardRef<ComposerHandle, ComposerProps>(
  function Composer(
    { draft, repName, isFirstResponse, responding, noting, onRespond, onNote },
    ref
  ) {
    const [tab, setTab] = useState<Tab>('response')
    const [responseBody, setResponseBody] = useState('')
    const [noteBody, setNoteBody] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [success, setSuccess] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [overwriteOpen, setOverwriteOpen] = useState(false)
    // A monotonic nonce whose change requests a scroll+focus of the active tab.
    const [focusReq, setFocusReq] = useState(0)

    const containerRef = useRef<HTMLDivElement>(null)
    const responseRef = useRef<HTMLTextAreaElement>(null)
    const noteRef = useRef<HTMLTextAreaElement>(null)
    const sendTriggerRef = useRef<HTMLButtonElement>(null)

    // Scroll the composer into view and focus the active tab's textarea whenever
    // a header action (or tab switch) requests it.
    useEffect(() => {
      if (focusReq === 0) return
      // Optional-call: jsdom has no scrollIntoView; it's a progressive nicety.
      containerRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      })
      const el = tab === 'response' ? responseRef.current : noteRef.current
      el?.focus()
    }, [focusReq, tab])

    useImperativeHandle(
      ref,
      () => ({
        activateResponse() {
          setError(null)
          setTab('response')
          // Pre-fill an EMPTY response buffer from the draft (starting material);
          // never overwrite text already typed.
          setResponseBody(prev =>
            prev.trim() === '' && draft !== null ? draft : prev
          )
          setFocusReq(n => n + 1)
        },
        activateNote() {
          setError(null)
          setTab('note')
          setFocusReq(n => n + 1)
        },
        useDraft() {
          if (draft === null) return
          setError(null)
          setTab('response')
          setResponseBody(prev => {
            // Explicit re-copy: confirm before overwriting non-empty, differing
            // text.
            if (prev.trim() !== '' && prev.trim() !== draft.trim()) {
              setOverwriteOpen(true)
              return prev
            }
            return draft
          })
          setFocusReq(n => n + 1)
        },
      }),
      [draft]
    )

    const hasRep = repName !== null
    const editState = draftEditedForDisplay(
      draft,
      responseBody,
      isFirstResponse
    )

    const closeConfirmRestoreFocus = () => {
      setConfirmOpen(false)
      // Restore focus to the trigger (stable fallback — the button exists while
      // the composer is mounted).
      sendTriggerRef.current?.focus()
    }

    const doSend = async () => {
      try {
        setError(null)
        await onRespond(responseBody)
        setResponseBody('')
        setConfirmOpen(false)
        setSuccess(
          hasRep
            ? `Sent to ${repName}. The ticket is now awaiting their confirmation.`
            : 'Response recorded. The ticket is now answered.'
        )
        sendTriggerRef.current?.focus()
      } catch (err) {
        // Business failure: keep the text, show the mapped copy inline.
        setConfirmOpen(false)
        setError(err instanceof Error ? err.message : 'Failed to send.')
        sendTriggerRef.current?.focus()
      }
    }

    const doNote = async () => {
      try {
        setError(null)
        await onNote(noteBody)
        setNoteBody('')
        setSuccess('Internal note added.')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add note.')
      }
    }

    const responseEmpty = responseBody.trim() === ''
    const noteEmpty = noteBody.trim() === ''

    return (
      <section
        ref={containerRef}
        className="card p-6"
        aria-labelledby="composer-heading"
      >
        <h2 id="composer-heading" className="sr-only">
          Respond or add an internal note
        </h2>

        {/* Tabs — Response default, Internal note yellow-walled. */}
        <div
          role="tablist"
          aria-label="Composer mode"
          className="mb-4 flex gap-2 border-b border-gray-200"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'response'}
            onClick={() => {
              setError(null)
              setTab('response')
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === 'response'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Response
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'note'}
            onClick={() => {
              setError(null)
              setTab('note')
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === 'note'
                ? 'border-yellow-500 text-yellow-800'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Internal note
          </button>
        </div>

        {success ? (
          <div
            role="status"
            className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
          >
            {success}
          </div>
        ) : null}

        {error ? (
          <div
            id="composer-error"
            role="alert"
            className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {error}
          </div>
        ) : null}

        {tab === 'response' ? (
          <div>
            <p className="mb-2 text-sm text-gray-600">
              {hasRep
                ? `Sends by email to ${repName}.`
                : 'This caller has no registered rep — the response will be recorded but not emailed.'}
            </p>
            <textarea
              ref={responseRef}
              value={responseBody}
              onChange={e => {
                setResponseBody(e.target.value)
                setSuccess(null)
              }}
              aria-label="Response to the rep"
              aria-describedby={error ? 'composer-error' : undefined}
              rows={6}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm leading-relaxed"
              placeholder="Write the answer the rep will receive…"
            />
            <div className="mt-3 flex justify-end">
              <button
                ref={sendTriggerRef}
                type="button"
                disabled={responseEmpty || responding}
                onClick={() => {
                  setSuccess(null)
                  setConfirmOpen(true)
                }}
                className="btn-primary"
              >
                Send response
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Yellow-walled note area — never confusable with a rep-visible
                reply. */}
            <div className="rounded-md border-l-4 border-yellow-200 bg-yellow-50 p-3">
              <p className="mb-2 text-xs font-semibold text-yellow-800">
                Internal — not visible to rep
              </p>
              <textarea
                ref={noteRef}
                value={noteBody}
                onChange={e => {
                  setNoteBody(e.target.value)
                  setSuccess(null)
                }}
                aria-label="Internal note (not visible to rep)"
                aria-describedby={error ? 'composer-error' : undefined}
                rows={5}
                className="w-full rounded-md border border-yellow-200 bg-white px-3 py-2 text-sm leading-relaxed"
                placeholder="Add a note for the team…"
              />
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                disabled={noteEmpty || noting}
                onClick={doNote}
                className="btn-outline"
              >
                {noting ? 'Adding…' : 'Add note'}
              </button>
            </div>
          </div>
        )}

        {/* Explicit re-copy overwrite confirm ("Use draft" over typed text). */}
        {overwriteOpen ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
            onKeyDown={e => {
              if (e.key === 'Escape') setOverwriteOpen(false)
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="overwrite-title"
              className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
            >
              <h3
                id="overwrite-title"
                className="mb-1 text-lg font-semibold text-gray-900"
              >
                Replace your draft response?
              </h3>
              <p className="mb-4 text-sm text-gray-600">
                This replaces the text you&apos;ve written with Yak&apos;s
                draft.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setOverwriteOpen(false)}
                  className="btn-outline"
                >
                  Keep mine
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (draft !== null) setResponseBody(draft)
                    setOverwriteOpen(false)
                    setFocusReq(n => n + 1)
                  }}
                  className="btn-primary"
                >
                  Use draft
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {confirmOpen ? (
          <SendConfirm
            recipientName={repName}
            body={responseBody}
            editState={editState}
            sending={responding}
            onConfirm={doSend}
            onCancel={closeConfirmRestoreFocus}
          />
        ) : null}
      </section>
    )
  }
)
